import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {
  LicenseVersionModel,
  LicenseVersionsPricesModel,
  LicenseVersionsProductModel,
  LicenseVersionsWithOptionsModel,
} from '../models/LicenseVersionsModel'
import LicenseVersionsService from '../LicenseVersionsService'
import { changeLoading } from '../../Loading/store/loading.slice'
import { ALLOWED_LICENSES_IDS, MAX_PAGE_SIZE } from '@/config/constants'

export interface LicenseVersionsState {
  licensesDeviceSerial: LicenseVersionsProductModel[]
  licenseVersion: LicenseVersionModel | null
  prices: LicenseVersionsWithOptionsModel | null
}

const initialState: LicenseVersionsState = {
  licensesDeviceSerial: [],
  licenseVersion: null,
  prices: null,
}

export const createPricesThunk = createAsyncThunk(
  'licenseVersions/createPricesThunk',
  async (data: LicenseVersionsPricesModel, { rejectWithValue }) => {
    try {
      return await LicenseVersionsService.createPrices(data)
    } catch (errors) {
      return rejectWithValue(errors)
    }
  },
)

export const getDeviceSerialThunk = createAsyncThunk(
  'licenseVersions/getDeviceSerialThunk',
  async (deviceNumber: string, { rejectWithValue, dispatch }) => {
    try {
      const res = await LicenseVersionsService.getDeviceSerial(deviceNumber)
      dispatch(changeLoading(false))
      return res
    } catch (errors) {
      return rejectWithValue(errors)
    }
  },
)

export const licenseVersionsPriceWithOptionsThunk = createAsyncThunk(
  'licenseVersions/licenseVersionsPriceWithOptionsThunk',
  async (data: LicenseVersionsPricesModel, { rejectWithValue, dispatch }) => {
    try {
      const res = await LicenseVersionsService.licenseVersionsPriceWithOptions(
        data,
      )
      dispatch(changeLoading(false))
      return res
    } catch (errors) {
      return rejectWithValue(errors)
    }
  },
)

export const getLicenseVersionsByIdThunk = createAsyncThunk(
  'licenseVersions/getLicenseVersionsByIdThunk',
  async (id: string, { rejectWithValue }) => {
    try {
      return await LicenseVersionsService.getLicenseVersionsById(id)
    } catch (errors) {
      return rejectWithValue(errors)
    }
  },
)

export const getLicenseVersionsThunk = createAsyncThunk(
  'licenseVersions/getLicenseVersionsThunk',
  async (_, { rejectWithValue }) => {
    try {
      return await LicenseVersionsService.getLicenseVersionsByFilter({
        pageSize: MAX_PAGE_SIZE,
      })
    } catch (errors) {
      return rejectWithValue(errors)
    }
  },
)

export const licenseVersionsSlice = createSlice({
  name: 'licenseVersions',
  initialState,
  reducers: {
    setLicenseVersion(state, action) {
      state.licenseVersion = action.payload
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getDeviceSerialThunk.fulfilled, (state, action) => {
        state.licensesDeviceSerial = action.payload
      })

      .addCase(getDeviceSerialThunk.rejected, state => {
        state.licensesDeviceSerial = []
      })
      .addCase(getLicenseVersionsThunk.fulfilled, (state, action) => {
        state.licensesDeviceSerial = (action.payload || [])
          .filter(el => ALLOWED_LICENSES_IDS.includes(el.id))
          .map(el => {
            return {
              ...new LicenseVersionsProductModel(),
              licenseVersionId: el.id,
              licenseVersion: el,
            }
          })
      })

      .addCase(getLicenseVersionsThunk.rejected, state => {
        state.licensesDeviceSerial = []
      })

      .addCase(getLicenseVersionsByIdThunk.fulfilled, (state, action) => {
        state.licenseVersion = action.payload
      })

      .addCase(getLicenseVersionsByIdThunk.rejected, state => {
        state.licenseVersion = null
      })

      .addCase(
        licenseVersionsPriceWithOptionsThunk.fulfilled,
        (state, action) => {
          state.prices = action.payload
        },
      )

      .addCase(licenseVersionsPriceWithOptionsThunk.rejected, state => {
        state.prices = null
      })
  },
})

export const { actions, reducer } = licenseVersionsSlice
export const { setLicenseVersion } = actions
